import { useState, FormEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Grid,
  Button,
  Divider,
  styled,
  Alert,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import registrationService, {
  RuRegistrationRequest
} from 'src/services/registrationService';
import { useTranslation } from 'react-i18next';
import Footer from 'src/components/Footer';

// Styled components similar to Login page for consistent design
const MainContent = styled(Box)(
  ({ theme }) => `
    min-height: 100%;
    display: flex;
    flex: 1;
    overflow: auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: ${theme.colors.alpha.white[100]};
    position: relative;
    
    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, rgba(0, 203, 117, 0.03) 0%, rgba(120, 120, 250, 0.03) 100%);
      z-index: 0;
    }
    
    @keyframes slideInFromLeft {
      0% {
        transform: translateX(-100%) scale(0.9);
        opacity: 0;
      }
      70% {
        transform: translateX(10px) scale(1.02);
      }
      85% {
        transform: translateX(-5px) scale(1);
      }
      100% {
        transform: translateX(0) scale(1);
        opacity: 1;
      }
    }
    
    @keyframes fadeOut {
      0% {
        opacity: 1;
        transform: scale(1);
      }
      100% {
        opacity: 0;
        transform: scale(0.95);
      }
    }
    
    @keyframes iconPulse {
      0%, 100% {
        transform: scale(1);
        opacity: 1;
      }
      50% {
        transform: scale(1.2);
        opacity: 0.9;
      }
    }
    
    @keyframes progressBar {
      0% {
        width: 100%;
      }
      100% {
        width: 0%;
      }
    }
`
);

const RegistrationContainer = styled(Container)(
  ({ theme }) => `
    padding: ${theme.spacing(4, 3)};
    position: relative;
    z-index: 1;
`
);

const RegistrationCard = styled(Paper)(
  ({ theme }) => `
    border-radius: 16px;
    padding: ${theme.spacing(4)};
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    overflow: hidden;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 6px;
      height: 100%;
      background: ${theme.colors.gradients.accso2};
    }
`
);

const LogoWrapper = styled(Box)(
  ({ theme }) => `
    margin-bottom: ${theme.spacing(3)};
    display: flex;
    justify-content: center;
    
    img {
      width: auto;
      height: 105px;
      max-width: 160px;
      object-fit: contain;
    }
`
);

const StyledButton = styled(Button)(
  ({ theme }) => `
    text-transform: uppercase;
    font-weight: bold;
    height: 46px;
    border-radius: 10px;
    background: ${theme.colors.gradients.accso2};
    box-shadow: ${theme.colors.shadows.primary};
    
    &:hover {
      background: ${theme.colors.gradients.accso3};
      box-shadow: ${theme.colors.shadows.accent};
    }
`
);

const StyledTextField = styled(TextField)(
  ({ theme }) => `
    & .MuiOutlinedInput-root {
      border-radius: 10px;
      background-color: ${theme.colors.alpha.white[100]};
      
      &.Mui-focused .MuiOutlinedInput-notchedOutline {
        border-color: ${theme.palette.primary.main};
        border-width: 2px;
      }
    }
    
    & .MuiInputLabel-root.Mui-focused {
      color: ${theme.palette.primary.main};
    }
`
);

const SuccessAlert = styled(Alert)(
  ({ theme }) => `
    border-radius: 10px;
    margin-bottom: ${theme.spacing(3)};
    padding: ${theme.spacing(2)};
    background-color: rgba(46, 125, 50, 0.1);
    color: #2e7d32;
    border: 1px solid rgba(46, 125, 50, 0.3);
    
    & .MuiAlert-icon {
      color: #2e7d32;
    }
`
);

// Define our form state interface
interface FormState {
  // Company details
  companyName: string;
  vatNumber: string;
  address: string;
  city: string;
  zipCode: string;
  country: string;

  // Contact information
  adminName: string; // This doubles as the contact name
  contactEmail: string;
  contactPhone: string;

  // Admin account
  adminEmail: string;
  adminPassword: string;
  confirmPassword: string;

  // Additional info
  additionalInfo: string;
}

// Define our form error interface
interface FormErrors {
  companyName?: string;
  vatNumber?: string;
  address?: string;
  city?: string;
  zipCode?: string;
  country?: string;
  adminName?: string;
  contactEmail?: string;
  contactPhone?: string;
  adminEmail?: string;
  adminPassword?: string;
  confirmPassword?: string;
  additionalInfo?: string;
  form?: string;
}

function RtuRegistrationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  // Form state management
  const [formState, setFormState] = useState<FormState>({
    companyName: '',
    vatNumber: '',
    address: '',
    city: '',
    zipCode: '',
    country: '',
    adminName: '',
    contactEmail: '',
    contactPhone: '',
    adminEmail: '',
    adminPassword: '',
    confirmPassword: '',
    additionalInfo: ''
  });

  // Form error state
  const [errors, setErrors] = useState<FormErrors>({});

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value
    }));

    // Clear field-specific error when user edits the field
    if (errors[name as keyof FormErrors]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: undefined
      }));
    }
  };

  // Password validation
  const validatePassword = (password: string): boolean => {
    // At least 8 characters, with at least one digit, lowercase, uppercase, and special character
    const passwordRegex =
      /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[@#$%^&+=!])(?=\S+$).{8,}$/;
    return passwordRegex.test(password);
  };

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!formState.companyName.trim()) {
      newErrors.companyName = t('rtuRegistration.errors.companyNameRequired');
      isValid = false;
    }
    if (!formState.adminName.trim()) {
      newErrors.adminName = t('rtuRegistration.errors.contactNameRequired');
      isValid = false;
    }
    if (!formState.contactEmail.trim()) {
      newErrors.contactEmail = t('rtuRegistration.errors.contactEmailRequired');
      isValid = false;
    } else if (!validateEmail(formState.contactEmail)) {
      newErrors.contactEmail = t('rtuRegistration.errors.invalidEmail');
      isValid = false;
    }
    if (!formState.adminEmail.trim()) {
      newErrors.adminEmail = t('rtuRegistration.errors.adminEmailRequired');
      isValid = false;
    } else if (!validateEmail(formState.adminEmail)) {
      newErrors.adminEmail = t('rtuRegistration.errors.invalidEmail');
      isValid = false;
    }
    if (!formState.adminPassword) {
      newErrors.adminPassword = t('rtuRegistration.errors.passwordRequired');
      isValid = false;
    } else if (!validatePassword(formState.adminPassword)) {
      newErrors.adminPassword = t('rtuRegistration.errors.passwordStrength');
      isValid = false;
    }
    if (formState.adminPassword !== formState.confirmPassword) {
      newErrors.confirmPassword = t(
        'rtuRegistration.errors.passwordsDoNotMatch'
      );
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };

  // Form submission handler
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Clear previous submission states
    setSubmitSuccess(false);
    setSubmitError('');

    // Validate form
    if (!validateForm()) {
      return;
    }

    // Prepare registration data
    const registrationData: RuRegistrationRequest = {
      companyName: formState.companyName,
      vatNumber: formState.vatNumber,
      address: formState.address,
      city: formState.city,
      zipCode: formState.zipCode,
      country: formState.country,
      contactEmail: formState.contactEmail,
      contactPhone: formState.contactPhone || undefined,
      adminName: formState.adminName,
      adminEmail: formState.adminEmail,
      adminPassword: formState.adminPassword,
      additionalInfo: formState.additionalInfo || undefined
    };

    // Set loading state
    setIsSubmitting(true);

    try {
      // Submit registration
      await registrationService.submitRuRegistration(registrationData);
      setSubmitSuccess(true);
      // Success notification will be shown on the login page

      // Reset form after successful submission
      setFormState({
        companyName: '',
        vatNumber: '',
        address: '',
        city: '',
        zipCode: '',
        country: '',
        adminName: '',
        contactEmail: '',
        contactPhone: '',
        adminEmail: '',
        adminPassword: '',
        confirmPassword: '',
        additionalInfo: ''
      });

      // Navigate to login page with success state - notification will be shown there
      navigate('/login', { state: { registrationSuccess: true } });
    } catch (error) {
      // console.error('Registration failed:', error);

      // Set error state based on the error response
      if (error.response?.data?.message) {
        setSubmitError(error.response.data.message);
      } else if (error.message) {
        setSubmitError(error.message);
      } else {
        setSubmitError(
          'An error occurred while submitting your registration. Please try again.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('rtuRegistration.pageTitle')}</title>
      </Helmet>
      <MainContent>
        <RegistrationContainer maxWidth="md">
          <RegistrationCard elevation={10}>
            <LogoWrapper>
              <img
                src="/static/images/logo/logo.png"
                alt={t('rtuRegistration.logoAlt')}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = '/static/images/logo/logo.svg';
                }}
              />
            </LogoWrapper>

            <Typography
              variant="h3"
              align="center"
              sx={{
                mb: 1,
                fontWeight: 600,
                color: 'primary.main'
              }}
            >
              {t('rtuRegistration.heading')}
            </Typography>

            <Typography
              variant="subtitle1"
              align="center"
              sx={{
                mb: 4,
                color: 'text.secondary'
              }}
            >
              {t('rtuRegistration.subheading')}
            </Typography>

            {/* Success Message */}
            {submitSuccess && (
              <SuccessAlert
                icon={<CheckCircleIcon fontSize="inherit" />}
                severity="success"
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {t('rtuRegistration.successTitle')}
                </Typography>
                <Typography variant="body2">
                  {t('rtuRegistration.successMessage')}
                </Typography>
              </SuccessAlert>
            )}

            {/* Error Message */}
            {submitError && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: '10px',
                  '& .MuiAlert-icon': { color: '#d32f2f' }
                }}
              >
                {submitError}
              </Alert>
            )}

            <form onSubmit={handleSubmit} noValidate>
              {/* Company Information Section */}
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  color: 'primary.main',
                  fontWeight: 500
                }}
              >
                {t('rtuRegistration.companyInfo')}
              </Typography>

              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label={t('rtuRegistration.companyName')}
                    variant="outlined"
                    required
                    name="companyName"
                    value={formState.companyName}
                    onChange={handleInputChange}
                    error={!!errors.companyName}
                    helperText={errors.companyName}
                    disabled={isSubmitting || submitSuccess}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label={t('rtuRegistration.vatNumber')}
                    variant="outlined"
                    name="vatNumber"
                    value={formState.vatNumber}
                    onChange={handleInputChange}
                    error={!!errors.vatNumber}
                    helperText={errors.vatNumber}
                    disabled={isSubmitting || submitSuccess}
                  />
                </Grid>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label={t('rtuRegistration.address')}
                    variant="outlined"
                    name="address"
                    value={formState.address}
                    onChange={handleInputChange}
                    error={!!errors.address}
                    helperText={errors.address}
                    disabled={isSubmitting || submitSuccess}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label={t('rtuRegistration.city')}
                    variant="outlined"
                    name="city"
                    value={formState.city}
                    onChange={handleInputChange}
                    error={!!errors.city}
                    helperText={errors.city}
                    disabled={isSubmitting || submitSuccess}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <StyledTextField
                    fullWidth
                    label={t('rtuRegistration.zipCode')}
                    variant="outlined"
                    name="zipCode"
                    value={formState.zipCode}
                    onChange={handleInputChange}
                    error={!!errors.zipCode}
                    helperText={errors.zipCode}
                    disabled={isSubmitting || submitSuccess}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <StyledTextField
                    fullWidth
                    label={t('rtuRegistration.country')}
                    variant="outlined"
                    name="country"
                    value={formState.country}
                    onChange={handleInputChange}
                    error={!!errors.country}
                    helperText={errors.country}
                    disabled={isSubmitting || submitSuccess}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Contact Information Section */}
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  color: 'primary.main',
                  fontWeight: 500
                }}
              >
                {t('rtuRegistration.contactInfo')}
              </Typography>

              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label={t('rtuRegistration.contactName')}
                    variant="outlined"
                    required
                    name="adminName"
                    value={formState.adminName}
                    onChange={handleInputChange}
                    error={!!errors.adminName}
                    helperText={errors.adminName}
                    disabled={isSubmitting || submitSuccess}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label={t('rtuRegistration.contactEmail')}
                    variant="outlined"
                    required
                    type="email"
                    name="contactEmail"
                    value={formState.contactEmail}
                    onChange={handleInputChange}
                    error={!!errors.contactEmail}
                    helperText={errors.contactEmail}
                    disabled={isSubmitting || submitSuccess}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label={t('rtuRegistration.contactPhone')}
                    variant="outlined"
                    name="contactPhone"
                    value={formState.contactPhone}
                    onChange={handleInputChange}
                    error={!!errors.contactPhone}
                    helperText={errors.contactPhone}
                    disabled={isSubmitting || submitSuccess}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Admin Account Section */}
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  color: 'primary.main',
                  fontWeight: 500
                }}
              >
                {t('rtuRegistration.adminAccount')}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mb: 3,
                  color: 'text.secondary'
                }}
              >
                {t('rtuRegistration.adminAccountInfo')}
              </Typography>

              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label={t('rtuRegistration.adminEmail')}
                    variant="outlined"
                    required
                    type="email"
                    name="adminEmail"
                    value={formState.adminEmail}
                    onChange={handleInputChange}
                    error={!!errors.adminEmail}
                    helperText={errors.adminEmail}
                    disabled={isSubmitting || submitSuccess}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label={t('rtuRegistration.adminPassword')}
                    variant="outlined"
                    required
                    type="password"
                    name="adminPassword"
                    value={formState.adminPassword}
                    onChange={handleInputChange}
                    error={!!errors.adminPassword}
                    helperText={errors.adminPassword}
                    disabled={isSubmitting || submitSuccess}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label={t('rtuRegistration.confirmPassword')}
                    variant="outlined"
                    required
                    type="password"
                    name="confirmPassword"
                    value={formState.confirmPassword}
                    onChange={handleInputChange}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword}
                    disabled={isSubmitting || submitSuccess}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Additional Information Section */}
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  color: 'primary.main',
                  fontWeight: 500
                }}
              >
                {t('rtuRegistration.additionalInfo')}
              </Typography>

              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label={
                      t('rtuRegistration.additionalInfo') +
                      ' ' +
                      t('common.optional')
                    }
                    variant="outlined"
                    multiline
                    rows={4}
                    name="additionalInfo"
                    value={formState.additionalInfo}
                    onChange={handleInputChange}
                    error={!!errors.additionalInfo}
                    helperText={errors.additionalInfo}
                    disabled={isSubmitting || submitSuccess}
                  />
                </Grid>
              </Grid>

              {/* Submit Button */}
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <StyledButton
                  variant="contained"
                  color="primary"
                  size="large"
                  type="submit"
                  sx={{ minWidth: 200 }}
                  disabled={isSubmitting || submitSuccess}
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress
                        size={24}
                        sx={{ color: 'white', mr: 1 }}
                      />
                      {t('rtuRegistration.submitting')}
                    </>
                  ) : (
                    t('rtuRegistration.submit')
                  )}
                </StyledButton>
              </Box>
            </form>

            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('rtuRegistration.alreadyHaveAccount')}{' '}
                <Button
                  href="/login"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline'
                    },
                    p: 0,
                    minWidth: 'auto',
                    verticalAlign: 'baseline'
                  }}
                >
                  {t('rtuRegistration.signIn')}
                </Button>
              </Typography>
            </Box>

            <Footer />
          </RegistrationCard>
        </RegistrationContainer>
      </MainContent>
    </>
  );
}

export default RtuRegistrationPage;
